import { useState, useEffect, useCallback } from "react";
import { Bell, BellOff } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map((c) => c.charCodeAt(0)));
}

export default function NotificationBell() {
  const [permission, setPermission] = useState("default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return;
    setPermission(Notification.permission);
    // Check existing subscription
    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        setIsSubscribed(!!sub);
      });
    });
  }, []);

  const subscribe = useCallback(async () => {
    setLoading(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        setLoading(false);
        return;
      }
      // Get VAPID key from backend
      const res = await fetch(`${BACKEND_URL}/api/push/vapid-public-key`);
      const { public_key } = await res.json();
      const applicationServerKey = urlBase64ToUint8Array(public_key);

      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // Get email from localStorage if available (from my-blocks or buy flows)
      const email =
        localStorage.getItem("vp_email") ||
        sessionStorage.getItem("customer_email") ||
        "";

      await fetch(`${BACKEND_URL}/api/push/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subscription: subscription.toJSON(), email }),
      });

      setIsSubscribed(true);
    } catch (err) {
      console.error("Push subscription failed:", err);
    }
    setLoading(false);
  }, []);

  const unsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        await fetch(`${BACKEND_URL}/api/push/unsubscribe`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        });
        await subscription.unsubscribe();
      }
      setIsSubscribed(false);
    } catch (err) {
      console.error("Push unsubscribe failed:", err);
    }
    setLoading(false);
  }, []);

  // Don't render if push not supported
  if (!("Notification" in window) || !("serviceWorker" in navigator)) return null;
  // Don't render if permanently denied
  if (permission === "denied") return null;

  const handleClick = isSubscribed ? unsubscribe : subscribe;
  const Icon = isSubscribed ? Bell : BellOff;
  const title = isSubscribed ? "Notifications on — click to disable" : "Enable notifications";

  return (
    <button
      data-testid="notification-bell"
      onClick={handleClick}
      disabled={loading}
      title={title}
      className="relative flex items-center justify-center w-9 h-9 rounded-full transition-colors hover:bg-[#14B8A6]/10"
      style={{ opacity: loading ? 0.5 : 1 }}
    >
      <Icon size={18} className={isSubscribed ? "text-[#14B8A6]" : "text-[#94A3B8]"} />
      {isSubscribed && (
        <span
          className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#14B8A6]"
          style={{ boxShadow: "0 0 0 2px white" }}
        />
      )}
    </button>
  );
}
